import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { SignUpData, UserRole, Department } from '../../types/auth';
import { generateAvatarUrl } from '../../utils/avatar';
import { logger } from '../../utils/logger';
import toast from 'react-hot-toast';

export const useSignUp = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const signUp = async (data: SignUpData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Debugging: Log Supabase Instance
      console.log("🔥 Supabase Instance:", supabase);
      if (!supabase) {
        throw new Error("🚨 Supabase is undefined! Check the import in useSignUp.ts.");
      }

      // Input validation
      if (!data.email || !data.password || !data.name) {
        throw new Error('All fields are required');
      }

      // Default role and department for new users
      const defaultRole: UserRole = 'EMPLOYEE';
      const defaultDepartment: Department = 'SALES';

      // Generate avatar URL
      const avatar = generateAvatarUrl(data.email);

      // Debugging: Log Sign-Up Attempt
      logger.info('🔥 Starting sign up for:', data.email);

      // Create auth user with metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name,
            role: defaultRole,
            department: defaultDepartment,
            avatar
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (authError) {
        logger.error('❌ Auth signup error:', authError);
        throw new Error(authError.message || 'Failed to create account');
      }

      if (!authData?.user) {
        throw new Error('❌ No user data returned');
      }

      // Insert profile into Supabase database
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          email: data.email,
          name: data.name,
          role: defaultRole,
          department: defaultDepartment,
          avatar,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (profileError) {
        logger.error('❌ Profile creation error:', profileError);
        throw new Error('Failed to create user profile');
      }

      logger.info('✅ Sign up successful:', data.email);
      toast.success('Account created successfully! Please sign in.');
      navigate('/signin');

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create account';
      setError(message);
      toast.error(message);
      logger.error('Sign up error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return { signUp, isLoading, error };
};
